import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  Clock,
  Loader2,
  CheckCircle2,
  Info,
  Calendar,
  Plus
} from "lucide-react";
import { updateProvider, getNormalizedAvailabilities } from "../../services/providerService";

interface Props {
  providerData: any;
  onClose: () => void;
  onSuccess?: (newProviderData: any) => void;
}

interface FormState {
  day: string;
  from_time: string;
  to_time: string;
}

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const ProviderAvailabilityModal: React.FC<Props> = ({
  providerData,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState<FormState>({
    day: "Monday",
    from_time: "09:00",
    to_time: "17:00",
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const generateSlotsForQueue = (queue: {from_time: string, to_time: string}[]) => {
    const slotSet = new Set<string>();
    
    queue.forEach(q => {
       const [fromH, fromM] = q.from_time.split(':').map(Number);
       const [toH, toM] = q.to_time.split(':').map(Number);
       
       let current = new Date();
       current.setHours(fromH, fromM, 0, 0);
       
       let end = new Date();
       end.setHours(toH, toM, 0, 0);
       
       while(current < end) {
         const h = String(current.getHours()).padStart(2, "0");
         const m = String(current.getMinutes()).padStart(2, "0");
         slotSet.add(`${h}:${m}`);
         current.setMinutes(current.getMinutes() + 15);
       }
    });

    return Array.from(slotSet).sort((a,b) => a.localeCompare(b));
  };

  const validate = (): string | null => {
    if (!form.from_time) return "Please select a start time.";
    if (!form.to_time) return "Please select an end time.";
    if (form.from_time >= form.to_time) return "End time must be after start time.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);

      const normalizedAvailabilities = getNormalizedAvailabilities(providerData.availabilities);
      const newAvailabilities = normalizedAvailabilities.map((d: any) => {
        if (d.text === form.day) {
          const existingQueue = d.timing?.queue || [];
          // Add the new block to the queue
          const newQueue = [...existingQueue, { from_time: form.from_time, to_time: form.to_time }];
          
          // Generate 15-minute slots for the entire queue
          const generatedSlotTimes = generateSlotsForQueue(newQueue);
          const existingSlotTimes = d.timing?.slot?.map((s: any) => s.time) || [];
          
          // Combine generated slots with any existing slots
          const combinedTimes = Array.from(new Set([...existingSlotTimes, ...generatedSlotTimes])).sort((a, b) => a.localeCompare(b));
          const uniqueSlots = combinedTimes.map(t => ({ time: t, status: "available" }));
          
          return {
            ...d,
            day: true,
            timing: {
              ...d.timing,
              queue: newQueue,
              slot: uniqueSlots
            }
          };
        }
        return d;
      });

      const payload = {
        provider_id: String(providerData.id),
        availabilities: newAvailabilities
      };

      await updateProvider(providerData.id, payload);
      
      const newProviderData = {
        ...providerData,
        availabilities: newAvailabilities
      };

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess(newProviderData);
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(
        err?.message ||
          "Failed to save availability. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-slate-900 px-8 pt-8 pb-6 relative overflow-hidden">
          <Calendar
            size={120}
            className="absolute -right-6 -bottom-6 text-white/5 pointer-events-none"
          />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2.5 bg-emerald-500/20 rounded-xl inline-flex">
                <Plus size={22} className="text-emerald-400" />
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                <X size={18} />
              </button>
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight mb-1">
              Add Queue Availability
            </h2>
            <p className="text-white/50 text-sm font-medium">
              Manually add a block of time (From/To) for a specific day.
            </p>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {success ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">
                Availability Saved
              </h3>
              <p className="text-slate-400 text-sm font-medium">
                Your queue block has been added successfully.
              </p>
            </div>
          ) : (
            <>
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                  <Info size={16} className="shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              {/* Day Selection */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Calendar size={12} /> Day of the Week
                </label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => set("day", day)}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                        form.day === day
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-500 border-slate-200 hover:border-emerald-500/30 hover:text-emerald-600"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Clock size={12} /> From Time
                  </label>
                  <input
                    type="time"
                    value={form.from_time}
                    onChange={(e) => set("from_time", e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Clock size={12} /> To Time
                  </label>
                  <input
                    type="time"
                    value={form.to_time}
                    onChange={(e) => set("to_time", e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              {/* Summary Preview */}
              {form.from_time && form.to_time && (
                <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/10 space-y-1.5">
                  <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">
                    Queue Preview
                  </p>
                  <p className="text-sm font-bold text-slate-800">
                    📅 {form.day}   ⏱ {form.from_time} – {form.to_time}
                  </p>
                  <p className="text-xs text-slate-500 font-medium italic">
                    This block will be added to your queue consultation hours.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-12 rounded-2xl border border-slate-200 text-slate-500 font-bold text-sm hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !form.from_time || !form.to_time}
                  className="flex-[2] h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm shadow-lg shadow-emerald-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Save Availability
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default ProviderAvailabilityModal;

